import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface User {
  id: string;
  email: string;
  name: string;
  company_name: string;
  vat_number: string;
  profile_image?: string;
}

export interface Company {
  id: number | null;
  name: string;
  logo_url: string | null;
  logo: string | null;
  email?: string;
  phone?: string;
  website?: string;
}

export interface AuthState {
  user: User | null;
  company: Company | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // Tri-state cache of the active-contract gate result.
  //   null  = not yet checked this session (layout will check on mount)
  //   true  = customer has an active rental contract → portal unlocked
  //   false = no active contract → layout redirects to /no-active-contract
  hasActiveContract: boolean | null;
}

const initialState: AuthState = {
  user: null,
  company: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  hasActiveContract: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
      // Force the active-contract gate to re-check on every (re-)login
      // or session re-hydration. Without this, a stale `true` cached from
      // a previous session lets the layout skip the check and the user
      // lands on /dashboard even with no active contract.
      state.hasActiveContract = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      state.hasActiveContract = null;
    },
    updateProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    setCompany: (state, action: PayloadAction<Company>) => {
      state.company = action.payload;
    },
    setHasActiveContract: (state, action: PayloadAction<boolean | null>) => {
      state.hasActiveContract = action.payload;
    },
  },
});

export const {
  setUser,
  setLoading,
  setError,
  clearError,
  logout,
  updateProfile,
  setCompany,
  setHasActiveContract,
} = authSlice.actions;
export default authSlice.reducer;

